export type Authority = string

// We expect these to be migrated in resi-fe-btbhome and then to rename that service entirely to be something like
// 'morpheus'.
export type OwnerApp =
  | 'resi-fe-btbhome'
  | 'resi-fe-btbproperty'
  | 'resi-fe-btbqualdash'

export type NavGroup =
  | 'Onboarding & Access'
  | 'Asset Management'
  | 'Operations'
  | 'Configuration'
  | 'Insights'

interface BaseEntry {
  name: string
  legacyResource?: string // maps to design-system RESOURCES.* during migration
  read: ReadonlyArray<Authority>
  write?: ReadonlyArray<Authority>
  ownerApp: OwnerApp
  fullRefresh?: boolean
  navGroup?: NavGroup
  featureFlag?: string
  paletteNavigable?: boolean
  keywords?: ReadonlyArray<string>
}

export interface ResourceEntry extends BaseEntry {
  kind: 'resource'
  label: string
  paths: {
    index: string
    show?: string // ':id' template; omit if no member route
    new?: string
    edit?: string // ':id' template
  }
}

export interface PageEntry extends BaseEntry {
  kind: 'page'
  label: string
  path: string
}

export type ManifestEntry = ResourceEntry | PageEntry

/* ---------------------------------------------------------------------------
 * SOURCE OF TRUTH. `read`/`write` are the authoritative authorities for each
 * entry. Route authorization already resolves through here (see the router's
 * ManifestRoute -> usePermissions). These values were seeded from config's
 * RESOURCE_AUTHORITY_MAP (prod), which is being retired: remaining consumers
 * are the server XHR whitelist (server/index.js) and the ui-morpheus sidebar
 * (via UserPermissions in app.jsx). As those migrate to this manifest,
 * RESOURCE_AUTHORITY_MAP and its serialized config can be removed.
 * ------------------------------------------------------------------------- */

export const MANIFEST = {
  // ---- Onboarding & Access -------------------------------------------------
  user: {
    kind: 'resource',
    name: 'user',
    legacyResource: 'USERS_PAGE',
    label: 'Users',
    paths: {
      index: '/user-management',
      edit: '/user-management/:id/edit',
    },
    read: ['UM'],
    write: ['UM'],
    ownerApp: 'resi-fe-btbhome',
    navGroup: 'Onboarding & Access',
    paletteNavigable: true,
    keywords: ['users', 'accounts', 'people', 'manage'],
  },
  invitation: {
    kind: 'resource',
    name: 'invitation',
    legacyResource: 'INVITATIONS_PAGE',
    label: 'Invitations',
    paths: { index: '/invitations' },
    read: ['UM'],
    write: ['UM'],
    ownerApp: 'resi-fe-btbhome',
    navGroup: 'Onboarding & Access',
    featureFlag: 'fe.resi.invitations-management',
    paletteNavigable: true,
    keywords: ['invite', 'invites'],
  },
  communication: {
    kind: 'page',
    name: 'communications',
    legacyResource: 'COMMUNICATIONS_PAGE',
    label: 'Communications',
    path: '/communications',
    read: ['CLR'],
    write: ['CLW'],
    ownerApp: 'resi-fe-btbhome',
    navGroup: 'Onboarding & Access',
    featureFlag: 'fe.resi.is_communications_enabled',
    paletteNavigable: true,
    keywords: ['messages', 'notifications'],
  },
  organization: {
    kind: 'resource',
    name: 'organization',
    legacyResource: 'ORGANIZATIONS_PAGE',
    label: 'Organizations',
    paths: {
      index: '/organizations',
      new: '/organizations/create',
      edit: '/organizations/:id/edit',
    },
    read: ['MOA'],
    write: ['MOA'],
    ownerApp: 'resi-fe-btbhome',
    navGroup: 'Onboarding & Access',
    paletteNavigable: true,
    keywords: ['orgs', 'companies', 'sellers'],
  },
  // organization sub-screens share ORGANIZATIONS authority; modeled as pages
  organizationMembers: {
    kind: 'page',
    name: 'organizationMembers',
    legacyResource: 'ORGANIZATIONS_PAGE',
    label: 'Organization Members',
    path: '/organizations/members',
    read: ['MOA'],
    write: ['MOA'],
    ownerApp: 'resi-fe-btbhome',
    navGroup: 'Onboarding & Access',
    paletteNavigable: true,
    keywords: ['members'],
  },
  organizationActivityTimeline: {
    kind: 'page',
    name: 'organizationActivityTimeline',
    legacyResource: 'ORGANIZATIONS_PAGE',
    label: 'Activity Timeline',
    path: '/organizations/activitytimeline',
    read: ['MOA'],
    write: ['MOA'],
    ownerApp: 'resi-fe-btbhome',
    navGroup: 'Onboarding & Access',
    paletteNavigable: true,
    keywords: ['activity', 'timeline', 'history'],
  },

  // ---- Asset Management ----------------------------------------------------
  // Cross-app: served by resi-fe-btbproperty (fullRefresh).
  intakeHub: {
    kind: 'page',
    name: 'intakeHub',
    legacyResource: 'INTAKE_HUB_PAGE',
    label: 'Intake Hub',
    path: '/property/intake',
    read: ['ITK'],
    write: ['ITKU'],
    ownerApp: 'resi-fe-btbproperty',
    fullRefresh: true,
    navGroup: 'Asset Management',
    paletteNavigable: true,
    keywords: ['intake', 'onboard', 'new'],
  },
  transaction: {
    kind: 'resource',
    name: 'transaction',
    legacyResource: 'TRANSACTIONS_PAGE',
    label: 'Transactions',
    paths: {
      index: '/transactions',
      show: '/transactions/:transactionId',
    },
    // CONFIG says MPG. ROUTER gates with USERS_PAGE (UM) — BUG. Using config here.
    read: ['MPG'],
    write: [],
    ownerApp: 'resi-fe-btbhome',
    navGroup: 'Asset Management',
    featureFlag: 'fe.resi.enable-transactions-view',
    paletteNavigable: true,
    keywords: ['transactions', 'deals'],
  },
  property: {
    kind: 'resource',
    name: 'property',
    legacyResource: 'PROPERTIES_PAGE',
    label: 'Properties',
    paths: { index: '/property/properties' },
    read: ['MPG'],
    write: [],
    ownerApp: 'resi-fe-btbproperty',
    fullRefresh: true,
    navGroup: 'Asset Management',
    paletteNavigable: true,
    keywords: ['properties', 'assets', 'real estate'],
  },
  listing: {
    kind: 'resource',
    name: 'listing',
    legacyResource: 'LISTINGS_PAGE',
    label: 'Listings',
    paths: { index: '/property/listings' },
    read: ['MLST'],
    write: [],
    ownerApp: 'resi-fe-btbproperty',
    fullRefresh: true,
    navGroup: 'Asset Management',
    paletteNavigable: true,
    keywords: ['listings', 'active', 'live'],
  },
  event: {
    kind: 'resource',
    name: 'event',
    legacyResource: 'EVENTS_PAGE',
    label: 'Events',
    paths: {
      index: '/schedule/events',
      new: '/schedule/events/create',
      edit: '/schedule/events/:id/edit',
    },
    read: ['ME'],
    write: ['MEM'],
    ownerApp: 'resi-fe-btbhome',
    navGroup: 'Asset Management',
    paletteNavigable: true,
    keywords: ['events', 'schedule', 'calendar', 'auction'],
  },
  venue: {
    kind: 'resource',
    name: 'venue',
    legacyResource: 'VENUES_PAGE',
    label: 'Venues',
    paths: {
      index: '/schedule/venues',
      new: '/schedule/venues/create',
      edit: '/schedule/venues/:id/edit',
    },
    read: ['MV'],
    write: ['MVM'],
    ownerApp: 'resi-fe-btbhome',
    navGroup: 'Asset Management',
    paletteNavigable: true,
    keywords: ['venues', 'locations', 'places'],
  },

  // ---- Operations ----------------------------------------------------------
  auctioneerDashboard: {
    kind: 'page',
    name: 'auctioneerDashboard',
    legacyResource: 'AUCTIONEER_DASHBOARD_PAGE',
    label: 'Auctioneer Dashboard',
    path: '/auctioneer-dashboard',
    read: ['OBR'],
    write: ['OBW'],
    ownerApp: 'resi-fe-btbqualdash',
    navGroup: 'Operations',
    paletteNavigable: true,
    keywords: ['auctioneer', 'dashboard', 'live', 'auction'],
  },
  liveAuctionTracker: {
    kind: 'page',
    name: 'liveAuctionTracker',
    legacyResource: 'AUCTIONEER_LIVE_AUCTION_TRACKER_PAGE',
    label: 'Live Auction Check In/Out',
    path: '/auctioneer-dashboard/live-auction-tracker',
    read: ['TRM'],
    ownerApp: 'resi-fe-btbqualdash',
    navGroup: 'Operations',
    featureFlag: 'fe.resi.is_live_auction_check_in_out_tracker_enabled',
    paletteNavigable: true,
    keywords: ['live', 'auction', 'tracker', 'check in', 'check out'],
  },
  contracting: {
    kind: 'page',
    name: 'contracting',
    legacyResource: 'TRANSACTION_MANAGER_PAGE',
    label: 'Contracting',
    path: '/property/contracting',
    read: ['TRM'],
    ownerApp: 'resi-fe-btbproperty',
    fullRefresh: true,
    navGroup: 'Operations',
    paletteNavigable: true,
    keywords: ['contracting', 'contracts'],
  },
  closing: {
    kind: 'page',
    name: 'closing',
    legacyResource: 'TRANSACTION_MANAGER_PAGE',
    label: 'Closing',
    path: '/property/closings',
    read: ['TRM'],
    ownerApp: 'resi-fe-btbproperty',
    fullRefresh: true,
    navGroup: 'Operations',
    paletteNavigable: true,
    keywords: ['closing', 'closings'],
  },

  // ---- Configuration -------------------------------------------------------
  eventTemplate: {
    kind: 'resource',
    name: 'eventTemplate',
    legacyResource: 'EVENT_TEMPLATES_PAGE',
    label: 'Event Templates',
    paths: {
      index: '/schedule/event-templates',
      new: '/schedule/event-templates/create',
      edit: '/schedule/event-templates/:id/edit',
    },
    read: ['MET'],
    write: ['METM'],
    ownerApp: 'resi-fe-btbhome',
    navGroup: 'Configuration',
    paletteNavigable: true,
    keywords: ['event', 'templates'],
  },
  venueTemplate: {
    kind: 'resource',
    name: 'venueTemplate',
    legacyResource: 'VENUE_TEMPLATES_PAGE',
    label: 'Venue Templates',
    paths: {
      index: '/schedule/venue-templates',
      new: '/schedule/venue-templates/create',
      edit: '/schedule/venue-templates/:id/edit',
    },
    read: ['MVT'],
    write: ['MVTM'],
    ownerApp: 'resi-fe-btbhome',
    navGroup: 'Configuration',
    paletteNavigable: true,
    keywords: ['venue', 'templates'],
  },
  document: {
    kind: 'resource',
    name: 'document',
    legacyResource: 'DOCUMENTS_PAGE',
    label: 'Documents',
    paths: { index: '/property/documents' },
    read: ['MDOC'],
    write: ['MDM'],
    ownerApp: 'resi-fe-btbproperty',
    fullRefresh: true,
    navGroup: 'Configuration',
    paletteNavigable: true,
    keywords: ['documents', 'files', 'uploads'],
  },
  package: {
    kind: 'resource',
    name: 'package',
    legacyResource: 'PACKAGES_PAGE',
    label: 'Packages',
    paths: { index: '/property/packages' },
    read: ['MPKG'],
    write: ['MPM'],
    ownerApp: 'resi-fe-btbproperty',
    fullRefresh: true,
    navGroup: 'Configuration',
    paletteNavigable: true,
    keywords: ['packages', 'bundle'],
  },
  listingAttribute: {
    kind: 'resource',
    name: 'listingAttribute',
    legacyResource: 'LISTING_ATTRIBUTES_PAGE',
    label: 'Listing Attributes',
    paths: { index: '/property/listing-attributes' },
    // 4-tuple special case: [MLC, MLCM, '', ASCM] — read = MLC|ASCM(read), write = MLCM|ASCM
    read: ['MLC', 'ASCM'],
    write: ['MLCM', 'ASCM'],
    ownerApp: 'resi-fe-btbproperty',
    fullRefresh: true,
    navGroup: 'Configuration',
    paletteNavigable: true,
    keywords: ['listing', 'attributes', 'tags', 'metadata'],
  },
  marketingTag: {
    kind: 'resource',
    name: 'marketingTag',
    legacyResource: 'MARKETING_TAGS_PAGE',
    label: 'Marketing Tags',
    paths: { index: '/property/marketing-tags' },
    read: ['TM'],
    write: ['TM'],
    ownerApp: 'resi-fe-btbproperty',
    fullRefresh: true,
    navGroup: 'Configuration',
    paletteNavigable: true,
    keywords: ['marketing', 'tags', 'labels'],
  },
  tinyUrl: {
    kind: 'resource',
    name: 'tinyUrl',
    legacyResource: 'TINY_URL_PAGE',
    label: 'Tiny Urls',
    paths: {
      index: '/tiny-url',
      new: '/tiny-url/create',
      edit: '/tiny-url/:id/edit',
    },
    read: ['TUM'],
    write: ['TUM'],
    ownerApp: 'resi-fe-btbhome',
    navGroup: 'Configuration',
    paletteNavigable: true,
    keywords: ['tiny', 'url', 'short', 'links'],
  },
  clientAuthorization: {
    kind: 'page',
    name: 'clientAuthorization',
    legacyResource: 'CLIENT_AUTHORIZATION_PAGE',
    label: 'Client Authorization',
    path: '/client-authorization',
    read: ['CAM'],
    write: ['CAM'],
    ownerApp: 'resi-fe-btbhome',
    navGroup: 'Configuration',
    paletteNavigable: true,
    keywords: ['client', 'auth', 'oauth'],
  },

  // ---- Insights ------------------------------------------------------------
  morpheusLogs: {
    kind: 'page',
    name: 'morpheusLogs',
    legacyResource: 'MORPHEUS_LOGS_PAGE',
    label: 'Morpheus Logs',
    path: '/sync/morpheus-logs',
    read: ['MLOM'],
    ownerApp: 'resi-fe-btbhome',
    navGroup: 'Insights',
    paletteNavigable: true,
    keywords: ['logs', 'sync', 'morpheus', 'debug'],
  },
  financeTransactionHistory: {
    kind: 'page',
    name: 'financeTransactionHistory',
    legacyResource: 'FINANCE_PAGE',
    label: 'Transaction History',
    path: '/finance/transaction-history',
    read: ['FAR'],
    ownerApp: 'resi-fe-btbhome',
    navGroup: 'Insights',
    featureFlag: 'fe.resi.is_morpheus_finance_section_enabled',
    paletteNavigable: true,
    keywords: ['finance', 'transaction', 'history', 'money', 'payment'],
  },
  aiEvaluations: {
    kind: 'page',
    name: 'aiEvaluations',
    legacyResource: 'AI_EVALUATIONS_PAGE',
    label: 'AI Evaluations',
    path: '/ai-evaluations',
    read: ['AIM'],
    write: ['AIM'],
    ownerApp: 'resi-fe-btbhome',
    navGroup: 'Insights',
    paletteNavigable: true,
    keywords: ['ai', 'evaluations', 'cognition', 'ml', 'llm'],
  },

  // ---- Standalone / no nav group (reachable, but not in a header dropdown) --
  home: {
    kind: 'page',
    name: 'home',
    legacyResource: 'DASHBOARD_HOME_PAGE',
    label: 'Home',
    path: '/',
    read: ['ML'],
    ownerApp: 'resi-fe-btbhome',
    paletteNavigable: true,
    keywords: ['dashboard', 'home', 'main'],
  },
  chatNotebook: {
    kind: 'page',
    name: 'chatNotebook',
    legacyResource: 'DASHBOARD_HOME_PAGE',
    label: 'Chat Notebook',
    path: '/chat-notebook',
    read: ['ML'],
    ownerApp: 'resi-fe-btbhome',
    paletteNavigable: true,
    keywords: ['chat', 'notebook', 'ai', 'conversation'],
  },
  myWork: {
    kind: 'page',
    name: 'myWork',
    legacyResource: 'DASHBOARD_HOME_PAGE',
    label: 'My Work',
    path: '/my-work',
    read: ['ML'],
    ownerApp: 'resi-fe-btbhome',
    featureFlag: 'fe.resi.btbhome.enable-my-work-queue',
    paletteNavigable: true,
    keywords: ['my work', 'queue', 'tasks'],
  },
  communicationCenter: {
    kind: 'page',
    name: 'communicationCenter',
    legacyResource: 'COMMUNICATION_CENTER_PAGE',
    label: 'Communication Center',
    path: '/communication-center',
    read: ['ML'],
    write: ['ML'],
    ownerApp: 'resi-fe-btbhome',
    paletteNavigable: true,
    keywords: ['communication', 'center', 'sms', 'messaging', 'chat'],
  },
} as const

// The app this bundle IS. Any route owned by a different app must be reached
// with a hard navigation (full page load) so the owning MFE serves its bundle —
// an in-app history push would 404 (e.g. the auctioneer dashboard, owned by
// resi-fe-btbqualdash). Derive `fullRefresh` from ownerApp rather than
// hand-setting it per entry; the explicit `fullRefresh` flag remains only as an
// escape hatch to force a refresh on a same-app route.
export const CURRENT_APP: OwnerApp = 'resi-fe-btbhome'

export const requiresFullRefresh = (
  entry: Pick<BaseEntry, 'ownerApp' | 'fullRefresh'>
): boolean => entry.ownerApp !== CURRENT_APP || entry.fullRefresh === true

// Compile-time shape check (kept separate so it doesn't rely on `satisfies`,
// which the repo's Prettier parser can't yet handle). Preserves literal types.
type AssertManifestShape = typeof MANIFEST extends Record<string, ManifestEntry>
  ? true
  : never
const _assertManifestShape: AssertManifestShape = true
void _assertManifestShape

export type ResourceKey = keyof typeof MANIFEST
